import React, { useState, useRef, useEffect, useMemo } from 'react';

export interface GlassSelectOption {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
  subtitle?: string;
  disabled?: boolean;
}

export interface GlassSelectProps {
  options: GlassSelectOption[];
  value?: string | number | null;
  onChange: (value: any) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
  allowClear?: boolean;
  searchable?: boolean;
  id?: string;
  name?: string;
  placement?: 'bottom' | 'top' | 'auto';
}

export const GlassSelect: React.FC<GlassSelectProps> = ({
  options = [],
  value,
  onChange,
  placeholder = '-- Select an option --',
  disabled = false,
  className = '',
  style = {},
  icon,
  allowClear = false,
  searchable,
  id,
  name,
  placement = 'auto',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [computedPlacement, setComputedPlacement] = useState<'bottom' | 'top'>('bottom');
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto calculate dropdown direction (dropup vs dropdown)
  useEffect(() => {
    if (isOpen) {
      if (placement === 'top') {
        setComputedPlacement('top');
      } else if (placement === 'bottom') {
        setComputedPlacement('bottom');
      } else {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          if (spaceBelow < 280 && spaceAbove > spaceBelow) {
            setComputedPlacement('top');
          } else {
            setComputedPlacement('bottom');
          }
        }
      }
    }
  }, [isOpen, placement]);

  // Selected option object
  const selectedOption = useMemo(() => {
    return options.find((opt) => String(opt.value) === String(value));
  }, [options, value]);

  // Should we show search box? Default true if > 6 options
  const isSearchable = searchable ?? options.length > 6;

  // Filtered options based on search query
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const query = search.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        (opt.subtitle && opt.subtitle.toLowerCase().includes(query))
    );
  }, [options, search]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Auto focus search input on open
  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen, isSearchable]);

  const handleSelect = (option: GlassSelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={containerRef}
      id={id}
      data-name={name}
      className={`rm-glass-select-container position-relative ${isOpen ? 'open' : ''} ${className}`}
      style={{ width: '100%', zIndex: isOpen ? 1060 : 'auto', ...style }}
    >
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`rm-glass-select-trigger d-flex align-items-center justify-content-between w-100 ${
          isOpen ? 'active' : ''
        } ${disabled ? 'disabled' : ''}`}
        style={{
          height: '44px',
          padding: '0 14px',
          borderRadius: style?.borderRadius || '10px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
        }}
      >
        <div className="d-flex align-items-center gap-2 text-truncate me-2" style={{ flex: 1 }}>
          {selectedOption?.icon ? (
            <span className="rm-select-icon">{selectedOption.icon}</span>
          ) : icon ? (
            <span className="rm-select-icon">{icon}</span>
          ) : null}
          <span
            className={`text-truncate ${selectedOption ? 'rm-select-value' : 'rm-select-placeholder'}`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>

        <div className="d-flex align-items-center gap-1">
          {allowClear && selectedOption && !disabled && (
            <span
              onClick={handleClear}
              className="rm-select-clear-btn"
              title="Clear selection"
            >
              <i className="ti ti-x" />
            </span>
          )}
          <i
            className={`ti ti-chevron-down rm-select-chevron ${isOpen ? 'rotate' : ''}`}
          />
        </div>
      </button>

      {/* Glassmorphic Dropdown Menu */}
      {isOpen && (
        <div className={`rm-glass-select-menu shadow-lg ${computedPlacement === 'top' ? 'placement-top' : ''}`}>
          {/* Search Box if needed */}
          {isSearchable && (
            <div className="p-2 border-bottom rm-select-search-wrap">
              <div className="position-relative">
                <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="form-control form-control-sm ps-4 pe-2 rm-select-search-input"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="rm-select-options-list">
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-muted small">
                <i className="ti ti-search-off me-1" /> No options found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <div
                    key={String(opt.value)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(opt);
                    }}
                    onClick={() => handleSelect(opt)}
                    className={`rm-glass-select-item d-flex align-items-center justify-content-between ${
                      isSelected ? 'selected' : ''
                    } ${opt.disabled ? 'disabled' : ''}`}
                  >
                    <div className="d-flex align-items-center gap-2 text-truncate">
                      {opt.icon && <span className="rm-item-icon">{opt.icon}</span>}
                      <div className="d-flex flex-column text-truncate">
                        <span className="rm-item-label text-truncate">{opt.label}</span>
                        {opt.subtitle && (
                          <span className="rm-item-sub small text-muted text-truncate">
                            {opt.subtitle}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <i className="ti ti-check text-primary fw-bold ms-2 flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlassSelect;
